import { useCallback, useEffect } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import '../../global.scss';

const ResponsiveGridLayout = WidthProvider(Responsive);

const GridContainer = ({ handleChangeLayout, components, access, children }) => {
    const isAdminMode = Array.isArray(access) && access.includes('Admin');

    // Add CSS for proper grid item handling
    useEffect(() => {
        const styleId = 'grid-container-styles';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = `
                .grid-container .react-grid-item {
                    position: relative;
                    z-index: 1;
                    margin-bottom:70px;
                }
                .grid-container .react-grid-item.react-draggable-dragging {
                    z-index: 1000;
                }
                .grid-container .react-grid-item.react-resizable-resizing {
                    z-index: 1000;
                }
                .grid-container .react-grid-item > div {
                    width: 100%;
                    overflow: auto;
                    height: fit-content;
                    min-height: auto;
                }
                .grid-container .dynamic-table-responsive {
                    height: auto;
                    min-height: auto;
                }
                @media (max-width: 768px) {
                    .grid-container .react-grid-item > div {
                        overflow: visible;
                    }
                    .grid-container .dynamic-table-responsive {
                        max-height: none;
                        overflow-y: visible;
                    }
                }
            `;
            document.head.appendChild(style);
        }
    }, []);

    // Generate layout array for the current breakpoint (lg)
    const layout = components.map((comp, i) => {
        // Check if this component is a dynamic table by looking at its properties
        const isDynamicTable = comp.componentType === 'DynamicComponentsTable' ||
            (Array.isArray(comp.properties) && comp.properties.some(p => p.property === 'ComponentType' && p.values === 'DynamicComponentsTable'));

        // In fill mode, lock visual sequence to the sorted components array.
        // In admin mode, preserve saved x/y so drag+resize editing still behaves as before.
        const x = isAdminMode ? (comp.layout?.x ?? 0) : 0;
        const y = isAdminMode ? (comp.layout?.y ?? i) : i;
        const w = isAdminMode ? (comp.layout?.w ?? 2) : 12;
        const h = isDynamicTable ? Math.max(comp.layout?.h ?? 10, 10) : (comp.layout?.h ?? 10);

        return {
            i: comp.componentId ? comp.componentId.toString() : i.toString(),
            x,
            y,
            w,
            h, // Moderate height for tables
            minW: 1,
            minH: isDynamicTable ? 3 : 1, // Minimum height for tables
        };
    });

    const handleLayoutChange = useCallback((newLayout) => {
        if (!newLayout || newLayout.length === 0) return;
        handleChangeLayout && handleChangeLayout(newLayout);
    }, [handleChangeLayout]);

    return (
        <div style={{
            width: '100%',
            position: 'relative',
        }}>
            <ResponsiveGridLayout
                className="grid-container"
                onDragStop={handleLayoutChange}
                onResizeStop={handleLayoutChange}
                layouts={{ lg: layout }}
                breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                cols={{ lg: 12, md: 10, sm: 6, xs: 4, xxs: 2 }}
                rowHeight={5}
                margin={[10, 10]}
                containerPadding={[0, 0]}
                isDraggable={isAdminMode}
                isResizable={isAdminMode}
                compactType="vertical"
                preventCollision={false}
                verticalCompact={true}
                autoSize={true}
            >
                {components.map((comp, i) => (
                    <div
                        key={comp.componentId ? comp.componentId.toString() : i.toString()}
                        style={{
                            position: 'relative',
                            zIndex: 1,
                            // background: 'white',
                            // border: '1px solid #e0e0e0',
                            borderRadius: '4px',
                            overflow: 'auto'
                        }}
                    >
                        {children[i]}
                    </div>
                ))}
            </ResponsiveGridLayout>
        </div>
    );
};

export default GridContainer;
